import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, DollarSign, Ban, Sparkles } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';
import { AUTHOR, ORG } from '@/config/authorProfile';

const LAST_UPDATED = 'February 2, 2026';

export default function AffiliateDisclosurePage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <AppLogo size={36} />
            <p className="heading text-sm font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </p>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16 max-w-3xl">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-8"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-4">
            Legal · disclosure
          </p>
          <h1 className="heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
            Affiliate disclosure
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            The plain-English version of how {ORG.name} makes money, what that
            means for you, and where our editorial independence starts and stops.
          </p>
          <p className="text-xs text-slate-500 mt-4">Last updated: {LAST_UPDATED}</p>
        </motion.section>

        {/* The core disclosure — FTC 16 CFR Part 255 compliant */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6" data-testid="core-disclosure">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              The short version
            </h2>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              Some links on this site are affiliate links. If you click one and
              go on to <strong>book, sign up, or get approved</strong> with the
              partner on the other end, we may earn a commission at{' '}
              <strong>no additional cost to you</strong>. Nothing you pay
              changes; a small portion of the partner's revenue is shared with us.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              This is standard practice for independent publications like ours.
              It is disclosed here, in the footer of every page, and inline on
              individual articles that carry affiliate links. This complies with
              the U.S. Federal Trade Commission's guidance on endorsements
              (16 CFR Part 255).
            </p>
          </CardContent>
        </Card>

        {/* Programs we participate in */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-4">
              Programs we participate in
            </h2>
            <div className="space-y-4 text-sm text-slate-700">
              <ProgramRow
                name="SpotHero"
                type="Airport parking marketplace"
                where="Every airport guide page"
                what="We link to SpotHero's search for the airport you're viewing. If you book parking through that link, we earn a small commission."
              />
              <ProgramRow
                name="CardRatings"
                type="Credit-card affiliate network"
                where="Blog posts that mention specific travel credit cards"
                what="When a card is named (Chase Sapphire Reserve, Amex Platinum, etc.) we link to CardRatings' page for that card. If you apply and are approved, we earn a commission from the issuer."
              />
              <ProgramRow
                name="FlexOffers partner programs (rolling)"
                type="Multi-brand affiliate network"
                where="Where relevant (lounge memberships, travel insurance, TSA expediters)"
                what="These links will appear as they go live, and each will follow the same disclosure rules as SpotHero and CardRatings above."
              />
            </div>
          </CardContent>
        </Card>

        {/* Editorial independence */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              What affiliate revenue does <em>not</em> do
            </h2>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              An affiliate relationship never influences:
            </p>
            <ul className="text-sm text-slate-700 leading-relaxed space-y-1.5 list-disc pl-5">
              <li>Which airports we cover or how we score them</li>
              <li>Which credit cards, lounges, or products we recommend</li>
              <li>Whether we write positively or negatively about a product</li>
              <li>Which articles get published, updated, or unpublished</li>
              <li>The order that products or airports appear in a comparison</li>
            </ul>
            <p className="text-sm text-slate-700 leading-relaxed mt-3">
              Our full <Link to="/editorial-standards" className="text-blue-600 hover:underline">editorial standards</Link> spell this out in more detail. We also don't accept:
            </p>
            <ul className="text-sm text-slate-700 leading-relaxed space-y-1.5 list-disc pl-5 mt-2">
              <li>Sponsored posts or "advertorial" content</li>
              <li>Paid product placement inside articles</li>
              <li>"Review swaps" with other travel publications</li>
              <li>Pay-to-be-listed from lounge networks or airlines</li>
            </ul>
          </CardContent>
        </Card>

        {/* What ads look like */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Ban className="w-5 h-5 text-rose-600" />
              Display advertising
            </h2>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              We may run display advertising through Google AdSense or similar
              networks. When ads run, they are labeled by the ad network as{' '}
              <strong>"Ad,"</strong> <strong>"Sponsored,"</strong> or{' '}
              <strong>"Ads by Google."</strong> We never run:
            </p>
            <ul className="text-sm text-slate-700 leading-relaxed space-y-1 list-disc pl-5">
              <li>Interstitial ads that block content</li>
              <li>Pop-unders or auto-refreshing units</li>
              <li>Ads on trust or legal pages (this page, /privacy, /terms, /contact, /editorial-standards, /corrections)</li>
              <li>Ads on the prediction result page (transactional trust)</li>
            </ul>
          </CardContent>
        </Card>

        {/* Legal contact */}
        <div className="p-5 bg-slate-100 rounded-lg text-sm text-slate-600 leading-relaxed">
          <p className="mb-2">
            <strong className="text-slate-800">Questions about this disclosure?</strong>{' '}
            Email <a href={`mailto:${AUTHOR.email}`} className="text-blue-600 hover:underline">{AUTHOR.email}</a>.
          </p>
          <p>
            {ORG.name} is operated by {ORG.legalOwner}, {ORG.mailingAddress.city}, {ORG.mailingAddress.region}, {ORG.mailingAddress.country}.
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function ProgramRow({ name, type, where, what }) {
  return (
    <div className="pb-4 border-b border-slate-100 last:border-b-0 last:pb-0">
      <p className="font-semibold text-slate-900 mb-1">{name}</p>
      <p className="text-xs text-slate-500 mb-2">{type} · <span className="italic">Placement: {where}</span></p>
      <p>{what}</p>
    </div>
  );
}
